import * as THREE from 'three'

import { Location } from './Location'
import { normalToDirection } from './normalToDirection'

const _dirN = new THREE.Vector3()
const _tmpMat = new THREE.Matrix4()
const _tmpPos = new THREE.Vector3()
const _hits = []
const _raycaster = new THREE.Raycaster()

const _mesh = new THREE.Mesh()
_mesh.matrixAutoUpdate = false

const EPS = 1e-9

const loc1 = new Location()
const loc2 = new Location()

const _hit = {
  point: new THREE.Vector3(),
  normal: new THREE.Vector3(),
  distance: 0,
  block: null,
  chunk: null,
  voxel: new THREE.Vector3(),
}

function nextBoundaryDistanceX(x, dx, step) {
  if (step === 0) return Number.POSITIVE_INFINITY
  const boundary = step > 0 ? Math.floor(x + 0.5) + 0.5 : Math.floor(x + 0.5) - 0.5
  return (boundary - x) / dx
}

function nextBoundaryDistanceY(y, dy, step) {
  if (step === 0) return Number.POSITIVE_INFINITY
  const cell = Math.floor(y)
  const boundary = step > 0 ? cell + 1 : cell
  return (boundary - y) / dy
}

function nextBoundaryDistanceZ(z, dz, step) {
  if (step === 0) return Number.POSITIVE_INFINITY
  const boundary = step > 0 ? Math.floor(z + 0.5) + 0.5 : Math.floor(z + 0.5) - 0.5
  return (boundary - z) / dz
}

// Bottom-center origin placement for a block at voxel (ix,iy,iz)
function composeBlockMatrix(ix, iy, iz) {
  // bottom center world position
  const px = ix
  const py = iy
  const pz = iz
  return _tmpMat.compose(
    new THREE.Vector3(px, py, pz),
    new THREE.Quaternion(), // no rotation
    new THREE.Vector3(1, 1, 1) // scale geometry
  )
}

export function createRaycaster({ getBlockAt }) {
  /**
   * DDA over voxels sized BLOCK_SIZE, precise test via block.mesh.raycast (local origin = bottom center).
   */
  return function raycast(origin, direction, maxDistance) {
    if (maxDistance <= 0) return null

    _dirN.copy(direction)
    const len = _dirN.length()
    if (len < EPS) return null
    _dirN.divideScalar(len)

    // set up raycaster in WORLD space (we place each candidate mesh via matrixWorld)
    _raycaster.near = 0
    _raycaster.far = maxDistance
    _raycaster.set(origin, _dirN)
    _raycaster.firstHitOnly = true // three-mesh-bvh

    // starting voxel indices (size-aware)
    let ix = Math.floor(origin.x + 0.5)
    let iy = Math.floor(origin.y)
    let iz = Math.floor(origin.z + 0.5)

    const stepX = _dirN.x > 0 ? 1 : _dirN.x < 0 ? -1 : 0
    const stepY = _dirN.y > 0 ? 1 : _dirN.y < 0 ? -1 : 0
    const stepZ = _dirN.z > 0 ? 1 : _dirN.z < 0 ? -1 : 0

    // distances in WORLD meters to the first boundary along each axis
    let tMaxX = stepX ? Math.abs(nextBoundaryDistanceX(origin.x, _dirN.x, stepX)) : Number.POSITIVE_INFINITY
    let tMaxY = stepY ? Math.abs(nextBoundaryDistanceY(origin.y, _dirN.y, stepY)) : Number.POSITIVE_INFINITY
    let tMaxZ = stepZ ? Math.abs(nextBoundaryDistanceZ(origin.z, _dirN.z, stepZ)) : Number.POSITIVE_INFINITY

    // distances to cross one whole voxel (WORLD meters) per axis
    const tDeltaX = stepX ? Math.abs(1 / _dirN.x) : Number.POSITIVE_INFINITY
    const tDeltaY = stepY ? Math.abs(1 / _dirN.y) : Number.POSITIVE_INFINITY
    const tDeltaZ = stepZ ? Math.abs(1 / _dirN.z) : Number.POSITIVE_INFINITY

    let t = 0

    while (t <= maxDistance + EPS) {
      // map voxel -> chunk/block
      // fallback: use world center of the voxel
      // const cx = ix + 0.5
      // const cy = iy + 0.5
      // const cz = iz + 0.5
      loc1.voxel.set(ix, iy, iz)

      const block = getBlockAt(loc1) // loc1.chunkId to chunk then loc1.voxelIdx to block

      if (block && block.hitbox) {
        _mesh.geometry = block.hitbox.geometry
        // place the shared mesh at this voxel (bottom-center origin)
        _mesh.matrixWorld.copy(composeBlockMatrix(ix, iy, iz))

        _hits.length = 0
        _mesh.raycast(_raycaster, _hits)

        if (_hits.length) {
          const h = _hits[0]
          _hit.point.copy(h.point)
          _hit.normal.copy(h.face.normal)
          _hit.direction = normalToDirection(_hit.normal)
          _hit.distance = h.distance
          _hit.block = block
          _hit.voxel.set(ix, iy, iz)
          return _hit
        }
      }

      // advance to next voxel boundary
      if (tMaxX < tMaxY) {
        if (tMaxX < tMaxZ) {
          ix += stepX
          t = tMaxX
          tMaxX += tDeltaX
        } else {
          iz += stepZ
          t = tMaxZ
          tMaxZ += tDeltaZ
        }
      } else {
        if (tMaxY < tMaxZ) {
          iy += stepY
          t = tMaxY
          tMaxY += tDeltaY
        } else {
          iz += stepZ
          t = tMaxZ
          tMaxZ += tDeltaZ
        }
      }
    }

    return null
  }
}
